import { useCallback, useMemo, useRef, useState, type PointerEvent, type RefObject } from 'react';
import { getHardcodedMixSoundSrcById } from '../../../services/mixSounds.service';
import mixIconBird from '../../../assets/images/landing/sleep/soundMix/bird.svg';
import mixIconFlight from '../../../assets/images/landing/sleep/soundMix/flight.svg';
import mixIconForest from '../../../assets/images/landing/sleep/soundMix/forest.svg';
import mixIconRain from '../../../assets/images/landing/sleep/soundMix/rain.svg';
import mixIconValley from '../../../assets/images/landing/sleep/soundMix/valley.svg';
import mixIconWind from '../../../assets/images/landing/sleep/soundMix/wind.svg';
import { useMixAudioPlayback } from './mixAudioPlayback';
import { ALL_MIX_SOUND_IDS, type MixSoundId, useMixActiveSounds } from './soundMixActiveSounds';

export type { MixSoundId, MixSoundYEntry } from './soundMixActiveSounds';

const MIX_DIAMETER_MIN_PX = 100;
const MIX_DIAMETER_DEFAULT_PX = 180;
const MIX_DIAMETER_MAX_PX = 400;

const PAD_NODE_FRAME_CLASS =
  'flex select-none [-webkit-touch-callout:none] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full shadow-lg ring-4 ring-black/20';

type PadNodeDef = {
  id: MixSoundId;
  src: string;
  /** 초기 x 중심 (패드 안 %) — 로컬 상태 초기값 */
  defaultCxPct: number;
};

const PAD_NODES: PadNodeDef[] = [
  { id: 'rain', src: mixIconRain, defaultCxPct: 34 },
  { id: 'bird', src: mixIconBird, defaultCxPct: 14 },
  { id: 'wind', src: mixIconWind, defaultCxPct: 50 },
  { id: 'forest', src: mixIconForest, defaultCxPct: 58 },
  { id: 'valley', src: mixIconValley, defaultCxPct: 82 },
  { id: 'flight', src: mixIconFlight, defaultCxPct: 18 },
];

function buildInitialXPct(): Record<MixSoundId, number> {
  return Object.fromEntries(PAD_NODES.map((n) => [n.id, n.defaultCxPct])) as Record<MixSoundId, number>;
}

/**
 * 패드 좌표(0~100%)에서 원 **중심**을 클램프한다.
 * 원 지름의 절반(= 지름의 50%)까지 각 변 밖으로 나가도록 허용한다.
 */
function clampCenterPct(cx: number, cy: number, _diameterPct: number) {
  return {
    cx: Math.min(100, Math.max(0, cx)),
    cy: Math.min(100, Math.max(0, cy)),
  };
}

function clampDiameterPx(px: number): number {
  return Math.max(MIX_DIAMETER_MIN_PX, Math.min(MIX_DIAMETER_MAX_PX, Math.round(px)));
}

type DragSession = {
  pointerId: number;
  padEl: HTMLElement;
  id: MixSoundId;
  offsetXPct: number;
  offsetYPct: number;
};

type PinchSession = { startDist: number; startDiam: number };

function touchDistance(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function MixerPadNode({
  padRef,
  id,
  src,
  cxPct,
  cyPct,
  zIndex,
  active,
  diameterPx,
  onDrag,
  onPointerDownBegan,
  onDiameterPxChange,
}: {
  padRef: RefObject<HTMLDivElement>;
  id: MixSoundId;
  src: string;
  cxPct: number;
  cyPct: number;
  zIndex: number;
  active: boolean;
  diameterPx: number;
  onDrag: (id: MixSoundId, cx: number, cy: number) => void;
  onPointerDownBegan: (id: MixSoundId) => void;
  onDiameterPxChange: (id: MixSoundId, px: number) => void;
}) {
  const dragRef = useRef<DragSession | null>(null);
  const pinchRef = useRef<PinchSession | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());

  const beginDrag = (e: PointerEvent<HTMLDivElement>, pad: HTMLElement) => {
    const pr = pad.getBoundingClientRect();
    const pctX = ((e.clientX - pr.left) / pr.width) * 100;
    const pctY = ((e.clientY - pr.top) / pr.height) * 100;
    dragRef.current = {
      pointerId: e.pointerId,
      padEl: pad,
      id,
      offsetXPct: pctX - cxPct,
      offsetYPct: pctY - cyPct,
    };
  };

  const rebeginDragForPointer = (pointerId: number, pad: HTMLElement) => {
    const pt = pointersRef.current.get(pointerId);
    if (!pt) return;
    const pr = pad.getBoundingClientRect();
    const pctX = ((pt.x - pr.left) / pr.width) * 100;
    const pctY = ((pt.y - pr.top) / pr.height) * 100;
    dragRef.current = {
      pointerId,
      padEl: pad,
      id,
      offsetXPct: pctX - cxPct,
      offsetYPct: pctY - cyPct,
    };
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const pad = padRef.current;
    if (!pad) return;
    e.preventDefault();
    onPointerDownBegan(id);
    e.currentTarget.setPointerCapture(e.pointerId);

    const m = pointersRef.current;
    m.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (m.size === 1) {
      pinchRef.current = null;
      beginDrag(e, pad);
    } else if (m.size === 2) {
      dragRef.current = null;
      const pts = [...m.values()];
      const dist = touchDistance(pts[0], pts[1]);
      pinchRef.current = {
        startDist: Math.max(dist, 1),
        startDiam: diameterPx,
      };
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const pad = padRef.current;
    if (!pad) return;
    const m = pointersRef.current;
    if (!m.has(e.pointerId)) return;
    m.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (m.size >= 2 && pinchRef.current) {
      const pts = [...m.values()];
      if (pts.length < 2) return;
      const dist = touchDistance(pts[0], pts[1]);
      const ratio = dist / pinchRef.current.startDist;
      const next = clampDiameterPx(pinchRef.current.startDiam * ratio);
      if (next !== diameterPx) onDiameterPxChange(id, next);
      return;
    }

    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const pr = d.padEl.getBoundingClientRect();
    const w = pr.width;
    const diamPct = (diameterPx / w) * 100;
    const nx = ((e.clientX - pr.left) / w) * 100 - d.offsetXPct;
    const ny = ((e.clientY - pr.top) / pr.height) * 100 - d.offsetYPct;
    const c = clampCenterPct(nx, ny, diamPct);
    onDrag(d.id, c.cx, c.cy);
  };

  const endPointer = (e: PointerEvent<HTMLDivElement>) => {
    const m = pointersRef.current;
    if (!m.has(e.pointerId)) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      return;
    }
    m.delete(e.pointerId);

    if (m.size < 2) pinchRef.current = null;

    if (m.size === 1) {
      const pad = padRef.current;
      if (pad) {
        const [remainingPid] = [...m.keys()];
        rebeginDragForPointer(remainingPid, pad);
      }
    } else if (m.size === 0) {
      dragRef.current = null;
    }

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  if (!active) return null;

  return (
    <div
      className={`absolute touch-none cursor-grab active:cursor-grabbing ${PAD_NODE_FRAME_CLASS}`}
      style={{ left: `${cxPct}%`, top: `${cyPct}%`, zIndex, width: diameterPx, height: diameterPx }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        className="pointer-events-none h-full w-full select-none object-cover [-webkit-touch-callout:none]"
      />
    </div>
  );
}

function MixerPad({
  activeSet,
  padRef,
  xPctById,
  yPctBySound,
  zStack,
  diameterPxById,
  onPadDrag,
  onPadPointerDown,
  onPadDiameterPxChange,
  showTapToPlayOverlay,
  onTapToPlay,
}: {
  activeSet: ReadonlySet<MixSoundId>;
  padRef: RefObject<HTMLDivElement>;
  xPctById: Record<MixSoundId, number>;
  yPctBySound: Record<MixSoundId, number>;
  zStack: readonly MixSoundId[];
  diameterPxById: Record<MixSoundId, number>;
  onPadDrag: (id: MixSoundId, cx: number, cy: number) => void;
  onPadPointerDown: (id: MixSoundId) => void;
  onPadDiameterPxChange: (id: MixSoundId, px: number) => void;
  showTapToPlayOverlay?: boolean;
  onTapToPlay?: () => void | Promise<void>;
}) {
  return (
    <div
      ref={padRef}
      className="relative mx-auto aspect-square w-full max-w-[min(90vw,841px)] shrink-0 select-none overflow-hidden border-2 border-[#324bc6] bg-[#101010] [-webkit-touch-callout:none]"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="pointer-events-none absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-[#3a3a3a]" />
      <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-[#3a3a3a]" />
      {PAD_NODES.map((node) => {
        const zi = zStack.indexOf(node.id);
        const zIndex = zi >= 0 ? 10 + zi : 0;
        return (
          <MixerPadNode
            key={node.id}
            padRef={padRef}
            id={node.id}
            src={node.src}
            cxPct={xPctById[node.id]}
            cyPct={yPctBySound[node.id]}
            zIndex={zIndex}
            active={activeSet.has(node.id)}
            diameterPx={diameterPxById[node.id] ?? MIX_DIAMETER_DEFAULT_PX}
            onDrag={onPadDrag}
            onPointerDownBegan={onPadPointerDown}
            onDiameterPxChange={onPadDiameterPxChange}
          />
        );
      })}
      {showTapToPlayOverlay && onTapToPlay ? (
        <div
          className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="믹스 소리 재생"
        >
          <button
            type="button"
            className="touch-manipulation rounded-full bg-[#324bc6] px-[clamp(28px,8vw,48px)] py-[clamp(14px,3.5vw,22px)] text-[clamp(18px,4.2vw,28px)] font-semibold text-white shadow-lg ring-2 ring-white/25 transition hover:brightness-110 active:brightness-95"
            onClick={() => void onTapToPlay()}
          >
            재생하기
          </button>
        </div>
      ) : null}
    </div>
  );
}

function IconSpeaker({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M38.5 12.25L22.75 24.5H14c-1.925 0-3.5 1.575-3.5 3.5v14c0 1.925 1.575 3.5 3.5 3.5h8.75l15.75 12.25c2.275 1.75 5.25.087 5.25-2.8V15.05c0-2.887-2.975-4.55-5.25-2.8z"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <path
        d="M47.25 26.25c3.5 3.5 3.5 14 0 17.5M52.5 19.25c5.833 6.417 5.833 25.083 0 31.5"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const SOUND_BUTTONS: { id: MixSoundId; label: string; bg: string }[] = [
  { id: 'rain', label: '빗소리', bg: 'bg-[#4fb0ff]' },
  { id: 'bird', label: '새소리', bg: 'bg-[#ff8ee5]' },
  { id: 'wind', label: '바람', bg: 'bg-[#5e79ff]' },
  { id: 'forest', label: '숲', bg: 'bg-[#15c801]' },
  { id: 'valley', label: '계곡 물', bg: 'bg-[#bce1ff]' },
  { id: 'flight', label: '비행기', bg: 'bg-[#e4e4e4]' },
];

function SoundButton({
  label,
  bg,
  pressed,
  onClick,
}: {
  label: string;
  bg: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex min-w-0 justify-center">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={pressed}
        className={`flex h-[clamp(48px,10.5vw,98px)] w-full min-w-0 max-w-full items-center justify-center rounded-[10px] px-1 ${bg} text-center text-[clamp(12px,2.9vw,40px)] font-medium leading-tight text-black shadow-sm transition hover:brightness-95 active:brightness-90 sm:px-2 sm:text-[clamp(18px,4vw,40px)] ${
          pressed ? '' : 'opacity-50'
        }`}
      >
        {label}
      </button>
    </div>
  );
}

function buildInitialDiameterPx(): Record<MixSoundId, number> {
  const o = {} as Record<MixSoundId, number>;
  for (const id of ALL_MIX_SOUND_IDS) o[id] = MIX_DIAMETER_DEFAULT_PX;
  return o;
}

/** 랜딩에서 믹스 UI(사운드 버튼·패드)를 체험하는 구간 */
export function SoundMixExperienceSection() {
  const { activeSet, toggle, isActive, yPctBySound, zStack, setSoundYPct, bringPadSoundToFront } =
    useMixActiveSounds();
  const padRef = useRef<HTMLDivElement>(null);
  const mixPosRef = useRef({ x: {} as Record<MixSoundId, number>, y: {} as Record<MixSoundId, number> });
  const [xPctById, setXPctById] = useState<Record<MixSoundId, number>>(buildInitialXPct);
  const [diameterPxById, setDiameterPxById] = useState<Record<MixSoundId, number>>(buildInitialDiameterPx);
  const mixHostedSrcById = useMemo(() => getHardcodedMixSoundSrcById(), []);

  const [tapToPlayDismissed, setTapToPlayDismissed] = useState(false);

  mixPosRef.current = { x: xPctById, y: yPctBySound };

  const { resumeFromUserGesture } = useMixAudioPlayback(activeSet, diameterPxById, mixHostedSrcById);

  const handlePadTapToPlay = useCallback(async () => {
    await resumeFromUserGesture();
    setTapToPlayDismissed(true);
  }, [resumeFromUserGesture]);

  const onPadDrag = useCallback(
    (id: MixSoundId, cx: number, cy: number) => {
      const pad = padRef.current;
      if (!pad) return;
      const w = pad.getBoundingClientRect().width;
      if (w <= 0) return;
      const dpx = diameterPxById[id] ?? MIX_DIAMETER_DEFAULT_PX;
      const dPct = (dpx / w) * 100;
      const c = clampCenterPct(cx, cy, dPct);
      setXPctById((prev) => ({ ...prev, [id]: c.cx }));
      setSoundYPct(id, c.cy, dPct);
    },
    [setSoundYPct, diameterPxById],
  );

  const onPadDiameterPxChange = useCallback(
    (id: MixSoundId, px: number) => {
      const dpx = clampDiameterPx(px);
      setDiameterPxById((prev) => (prev[id] === dpx ? prev : { ...prev, [id]: dpx }));
      const pad = padRef.current;
      if (!pad) return;
      const w = pad.getBoundingClientRect().width;
      if (w <= 0) return;
      const dPct = (dpx / w) * 100;
      const { x: xMap, y: yMap } = mixPosRef.current;
      const cx = xMap[id];
      const cy = yMap[id];
      const c = clampCenterPct(cx, cy, dPct);
      if (c.cx !== cx) setXPctById((prev) => ({ ...prev, [id]: c.cx }));
      if (c.cy !== cy) setSoundYPct(id, c.cy, dPct);
    },
    [setSoundYPct],
  );

  const onPadPointerDown = useCallback(
    (id: MixSoundId) => {
      bringPadSoundToFront(id);
    },
    [bringPadSoundToFront],
  );

  return (
    <section aria-label="사운드 믹스 체험">
      <p className="mt-6 text-center text-[clamp(16px,3.4vw,40px)] font-medium leading-[1.4] text-white">
        버튼을 눌러 추가하세요
      </p>

      <div className="mt-[104px] flex flex-wrap items-center justify-center gap-3 md:gap-4">
        <p className="text-center text-[clamp(20px,4.2vw,50px)] font-medium leading-[1.4] text-[#fbb12c]">
          ※ 소리를 켜주세요.
        </p>
        <IconSpeaker className="h-[clamp(40px,8vw,70px)] w-[clamp(40px,8vw,70px)] shrink-0 text-[#fbb12c]" />
      </div>

      <div className="mx-auto mt-10 grid w-full min-w-0 max-w-[742px] grid-cols-3 gap-x-2 gap-y-3 sm:gap-x-[44px] sm:gap-y-3">
        {SOUND_BUTTONS.map(({ id, label, bg }) => (
          <SoundButton
            key={id}
            label={label}
            bg={bg}
            pressed={isActive(id)}
            onClick={() => {
              toggle(id);
              setTapToPlayDismissed(true);
            }}
          />
        ))}
      </div>

      <div className="mt-10 md:mt-14">
        <MixerPad
          padRef={padRef}
          activeSet={activeSet}
          xPctById={xPctById}
          yPctBySound={yPctBySound}
          zStack={zStack}
          diameterPxById={diameterPxById}
          onPadDrag={onPadDrag}
          onPadPointerDown={onPadPointerDown}
          onPadDiameterPxChange={onPadDiameterPxChange}
          showTapToPlayOverlay={!tapToPlayDismissed}
          onTapToPlay={handlePadTapToPlay}
        />
      </div>
    </section>
  );
}
